/* 0713-#32: 共通スライダーコンポーネント（受領原本 L803-832 の実装例をテーマ規約へ翻訳）。
 *
 * 責務: 横スクロールトラック（各セクション所有）への
 *   1) 薄型バー連動（thumb 幅 = cw/sw%・left = scrollLeft/sw%・バーのタップ/ドラッグでシーク）
 *   2) PC 前後ボタン（1枚分 = カード幅+gap の smooth 送り・端で is-disabled 減衰・
 *      縦位置はメディア枠基準 = 画像未登録の SVG placeholder でも img 非依存でズレない）
 *   3) PC grab ドラッグ（mouse のみ・rAF で scrollLeft 反映・hover/リンク誤発火抑止）
 *   4) 再計測（ResizeObserver = display:none→表示の 0→N 発火でタブ/reveal を自動吸収・
 *      refresh/reset API + CustomEvent 契約は非同期注入面〔G5〕の口）
 *
 * DOM 契約（snippets/tol-slider.liquid 参照）: ラッパー [data-tol-slider]（=トラックの
 * セレクタ・ラッパー内 querySelector で解決＝直下でなくてよい。非同期 swap コンテナ
 * 〔product-recommendations 等〕の介在可・0713-G5 で契約明確化）+ snippet のコントロール群。
 *
 * 原本実装例からの意図的乖離（DECISIONS 0713-#32 ADR）:
 * - インライン display 切替 → class（tol-slider--active）と media query の直交（S14b 継承）。
 * - mousedown/mousemove → Pointer Events（pointerType 判別で SP タッチをネイティブ慣性へ委譲）。
 * - ドラッグ確定（|dx|>5px）前は window 一時リスナーで追跡＝トラック外リリースでも状態が
 *   残留しない。確定後に setPointerCapture。
 * - 正常 pointerup は保留 rAF を止め最終位置を同期 flush（最終フレーム喪失防止）。
 *   pointercancel / lostpointercapture は flush しない。
 * - is-disabled は pointer-events:none にせず click を端ガードで no-op（背後カードへの透過防止）。
 *
 * custom element は作らない（tol-hero.js・旧 tol-scrollbar.js のアロー IIFE +
 * data-*-init ガード + AbortController teardown 前例を踏襲）。Dawn 本体無改修・tol- 隔離。
 */
(() => {
  /* 同一 src の <script> はセクション複数配置で複数回実行される（フェッチは1回・実行はタグごと） */
  if (window.__tolSliderLoaded) return;
  window.__tolSliderLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (root) => {
    root.querySelectorAll('[data-tol-slider]').forEach((wrap) => {
      if (wrap.dataset.tolSliderInit) return;
      let track = null;
      try {
        track = wrap.querySelector(wrap.dataset.tolSlider);
      } catch (_) {
        /* 不正セレクタは未紐付けのまま（コントロールは display:none 初期値で不可視） */
      }
      const bar = wrap.querySelector('[data-tol-slider-bar]');
      const thumb = bar && bar.querySelector('.tol-slider__thumb');
      const prevB = wrap.querySelector('[data-tol-slider-prev]');
      const nextB = wrap.querySelector('[data-tol-slider-next]');
      if (!track || !bar || !thumb || !prevB || !nextB) return;
      wrap.dataset.tolSliderInit = 'true';
      /* CSS フック（cursor:grab / ドラッグ中 > * pointer-events）。grab は JS 生存時のみ意味を持つ */
      track.setAttribute('data-tol-slider-track', '');

      const abort = new AbortController();
      const signal = abort.signal;

      const atStart = () => track.scrollLeft <= 1;
      const atEnd = () => track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

      const update = () => {
        const sw = track.scrollWidth;
        const cw = track.clientWidth;
        /* cw===0 = 非表示（hidden タブ / fail-closed ラッパー）中。全 UI 非表示に倒し、
           表示時は ResizeObserver（0→N）が再計測する */
        if (cw === 0 || sw <= cw + 2) {
          wrap.classList.remove('tol-slider--active');
          return;
        }
        wrap.classList.add('tol-slider--active');
        thumb.style.width = (cw / sw) * 100 + '%';
        thumb.style.left = (track.scrollLeft / sw) * 100 + '%';
        const s = atStart();
        const e = atEnd();
        prevB.classList.toggle('is-disabled', s);
        prevB.setAttribute('aria-disabled', s ? 'true' : 'false');
        nextB.classList.toggle('is-disabled', e);
        nextB.setAttribute('aria-disabled', e ? 'true' : 'false');
      };

      /* ボタン縦中央の基準 = data-tol-slider-media のメディア枠（トラック内の最初の1件）。
         img でなく枠基準＝画像未登録（SVG placeholder）でもズレない（原本逐語）。
         layout read を伴うため scroll では呼ばない（resize / RO / refresh のみ） */
      const positionNav = () => {
        let m = null;
        const sel = wrap.dataset.tolSliderMedia;
        if (sel) {
          try {
            m = track.querySelector(sel);
          } catch (_) {}
        }
        if (!m) m = track;
        const mr = m.getBoundingClientRect();
        if (mr.height === 0) return;
        const wr = wrap.getBoundingClientRect();
        const top = mr.top - wr.top + mr.height / 2 + 'px';
        prevB.style.top = top;
        nextB.style.top = top;
      };

      const refresh = () => {
        update();
        positionNav();
      };
      /* タブ切替時の先頭リセット + 再計算（原本逐語）。behavior は既定（auto）＝即時 */
      const reset = () => {
        track.scrollLeft = 0;
        refresh();
      };

      /* ---- バー: タップ/ドラッグでシーク（旧 tol-scrollbar.js の移植 + pointerId 管理） ---- */
      const seek = (clientX) => {
        const r = bar.getBoundingClientRect();
        if (r.width === 0) return;
        const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
        track.scrollLeft = ratio * (track.scrollWidth - track.clientWidth);
      };
      let barPointerId = null;
      bar.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          barPointerId = e.pointerId;
          try {
            bar.setPointerCapture(e.pointerId);
          } catch (_) {
            /* 無効な pointerId 等は capture なし（move 中の追従のみ劣化） */
          }
          seek(e.clientX);
          e.preventDefault();
        },
        { signal }
      );
      bar.addEventListener(
        'pointermove',
        (e) => {
          if (barPointerId === null || e.pointerId !== barPointerId) return;
          /* capture 失敗でバー外リリースを取りこぼしても、ボタン非押下 move では追従しない
             （mouse 限定の残留ガード。touch/pen は buttons=0 の move 自体が来ない） */
          if (e.pointerType === 'mouse' && (e.buttons & 1) === 0) {
            barPointerId = null;
            return;
          }
          seek(e.clientX);
        },
        { signal }
      );
      const barEnd = (e) => {
        if (e.pointerId === barPointerId) barPointerId = null;
      };
      bar.addEventListener('pointerup', barEnd, { signal });
      bar.addEventListener('pointercancel', barEnd, { signal });

      /* ---- 前後ボタン: 1枚分（カード幅 + gap）送り ---- */
      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      const nav = (dir) => {
        if (dir < 0 && atStart()) return; /* is-disabled は透過防止で pointer-events を殺さない＝端ガードは JS */
        if (dir > 0 && atEnd()) return;
        track.scrollBy({ left: dir * step(), behavior: reduced.matches ? 'auto' : 'smooth' });
      };
      prevB.addEventListener('click', () => nav(-1), { signal });
      nextB.addEventListener('click', () => nav(1), { signal });

      /* ---- PC grab ドラッグ（mouse のみ・SP/iPad タッチはネイティブ慣性へ全面委譲） ---- */
      const drag = { active: false, pointerId: 0, startX: 0, startScroll: 0, moved: false, raf: 0, target: 0 };
      let clickSuppress = false;

      const clearDrag = () => {
        if (!drag.active) return;
        drag.active = false;
        drag.moved = false;
        if (drag.raf) {
          cancelAnimationFrame(drag.raf);
          drag.raf = 0;
        }
        wrap.classList.remove('tol-slider--dragging');
        window.removeEventListener('pointermove', onWinMove);
        window.removeEventListener('pointerup', onWinUp);
        window.removeEventListener('pointercancel', onWinCancel);
        try {
          if (track.hasPointerCapture && track.hasPointerCapture(drag.pointerId)) {
            track.releasePointerCapture(drag.pointerId);
          }
        } catch (_) {}
      };

      const onWinMove = (e) => {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        const dx = e.clientX - drag.startX;
        if (!drag.moved) {
          /* |dx|>5px でドラッグ確定。それ未満は通常クリックとして生かす */
          if (Math.abs(dx) <= 5) return;
          drag.moved = true;
          try {
            track.setPointerCapture(drag.pointerId);
          } catch (_) {
            /* capture 失敗時は window リスナーで追従継続（劣化受容） */
          }
          wrap.classList.add('tol-slider--dragging');
        }
        /* ドラッグ中の scrollLeft 反映は rAF でまとめる（原本逐語）。一段予約＝二重予約防止 */
        drag.target = drag.startScroll - dx;
        if (!drag.raf) {
          drag.raf = requestAnimationFrame(() => {
            drag.raf = 0;
            track.scrollLeft = drag.target;
          });
        }
      };
      const onWinUp = (e) => {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        if (drag.moved) {
          /* 正常終了: 保留 rAF を止め最終位置を同期 flush（最終 move フレームの喪失防止） */
          if (drag.raf) {
            cancelAnimationFrame(drag.raf);
            drag.raf = 0;
          }
          track.scrollLeft = drag.target;
          clickSuppress = true;
          /* 保険解除。前提 = pointerup 由来の click は同一 input タスク内でタイマーより先に
             配送される（現行主要エンジンの実装挙動）。ウィンドウ外リリース等で click が
             発火しないケースでフラグが残り、次の正当クリックを食うのを防ぐ */
          setTimeout(() => {
            clickSuppress = false;
          }, 0);
        }
        clearDrag();
      };
      const onWinCancel = (e) => {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        /* 異常終了: flush しない（中断時の飛びを避ける） */
        if (drag.moved) {
          clickSuppress = true;
          setTimeout(() => {
            clickSuppress = false;
          }, 0);
        }
        clearDrag();
      };

      track.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          /* overflow なし（--active 不付与＝3枚以下等）や PC スライダー無効面（--pc-static）では
             状態機械を起動しない＝リンクタイル上の微動で clickSuppress が誤発しない */
          if (!wrap.classList.contains('tol-slider--active') || wrap.classList.contains('tol-slider--pc-static')) return;
          /* 前回の up/cancel 取りこぼし（ウィンドウ外リリース・alt+tab 等）の残留はここで巻き戻す */
          if (drag.active) clearDrag();
          /* preventDefault しない＝微動（<=5px）は通常クリックとして生きる */
          drag.pointerId = e.pointerId;
          drag.startX = e.clientX;
          drag.startScroll = track.scrollLeft;
          drag.moved = false;
          drag.active = true;
          /* 確定前にトラック外へ出ても up/cancel を取りこぼさない（状態残留防止） */
          window.addEventListener('pointermove', onWinMove, { signal });
          window.addEventListener('pointerup', onWinUp, { signal });
          window.addEventListener('pointercancel', onWinCancel, { signal });
        },
        { signal }
      );
      track.addEventListener(
        'lostpointercapture',
        () => {
          /* pointercancel を伴わない capture 喪失の直後 click もリンクへ流さない */
          if (drag.active && drag.moved) {
            clickSuppress = true;
            setTimeout(() => {
              clickSuppress = false;
            }, 0);
          }
          clearDrag();
        },
        { signal }
      );
      /* mouse pointerdown 中は <a>/<img> のネイティブ drag を常に抑止
         （確定前に始まると pointer stream が cancel されドラッグが死ぬ） */
      track.addEventListener(
        'dragstart',
        (e) => {
          if (drag.active) e.preventDefault();
        },
        { signal }
      );
      /* ドラッグ直後のリンク誤発火をキャプチャで抑止（原本逐語）。
         once にしない＝click 不発時に次の正当クリックを食わないようフラグ制 */
      track.addEventListener(
        'click',
        (e) => {
          if (clickSuppress) {
            e.preventDefault();
            e.stopPropagation();
            clickSuppress = false;
          }
        },
        { capture: true, signal }
      );

      /* ---- 再計測・ライフサイクル ---- */
      /* scroll 同期も rAF で一段化（ドラッグ側の scrollLeft 書き込みと対称・thumb 書き込みをフレームにまとめる） */
      let scrollRaf = 0;
      track.addEventListener(
        'scroll',
        () => {
          if (!scrollRaf) {
            scrollRaf = requestAnimationFrame(() => {
              scrollRaf = 0;
              update();
            });
          }
        },
        { signal, passive: true }
      );
      window.addEventListener('resize', refresh, { signal });
      /* display:none→表示（contentBox 0→N）で発火＝hidden タブ・fail-closed reveal を自動吸収。
         scrollWidth のみの変化（非同期カード注入）は非検知＝面側が refresh/scan を dispatch する
         （適用可否表・G5 の契約） */
      let ro = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(refresh);
        ro.observe(track);
      }
      /* bfcache 復元: ドラッグ状態・click 抑止フラグ・capture の残留を巻き戻す（PR-C 教訓） */
      window.addEventListener(
        'pageshow',
        (e) => {
          if (!e.persisted) return;
          clickSuppress = false;
          clearDrag();
          refresh();
        },
        { signal }
      );
      /* 公開 API（G5 の非同期注入面・タブ面が使う口） */
      wrap._tolSlider = { refresh, reset, abort };
      wrap.addEventListener('tol:slider:refresh', refresh, { signal });
      wrap.addEventListener('tol:slider:reset', reset, { signal });

      /* 初期計測: 即時 + 120ms 再実行（defer 実行直後はフォント/遅延画像で scrollWidth が動く） */
      const t = setTimeout(refresh, 120);
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        if (scrollRaf) {
          cancelAnimationFrame(scrollRaf);
          scrollRaf = 0;
        }
        if (ro) ro.disconnect();
        clearDrag();
        /* 同一ノードが再利用された場合（scan / section:load）に再 init 可能へ戻す */
        delete wrap.dataset.tolSliderInit;
      });
      refresh();
    });
  };

  init(document);
  /* 非同期に注入・差し替えられた新規 DOM の一括再スキャン口（init 済みは data-init で冪等） */
  document.addEventListener('tol:slider:scan', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-slider]').forEach((wrap) => {
      if (wrap._tolSlider) wrap._tolSlider.abort.abort();
    });
  });
})();
